import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Layout } from '@/components/layout';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useGetMe } from '@workspace/api-client-react';
import { useToast } from '@/hooks/use-toast';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import {
  ShoppingCart, Plus, Trash2, Loader2, FileText,
  ChevronRight, AlertTriangle, Search, Package,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ─────────────────────────────────────────────────────────────────────

interface ShopifyStore {
  id: string;
  nickname: string;
  storeUrl: string;
}

interface PoSupplier {
  id: string;
  name: string;
  email: string;
  autoSend: boolean;
  dropshipMode: boolean;
}

interface PurchaseOrder {
  id: string;
  poNumber: string;
  status: string;
  supplierName: string;
  itemCount: number;
  sourceOrderName: string | null;
  supplierDeletedNote: string | null;
  sentAt: string | null;
  createdAt: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
    sent: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
    received: 'bg-green-600/15 text-green-400 border border-green-600/20',
    cancelled: 'bg-muted text-muted-foreground border border-border',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${styles[status] ?? styles.draft}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function PoPurchaseOrders() {
  const token = useAuthStore((s) => s.token);
  const authOpts = useAuthHeaders();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: me, isLoading: meLoading } = useGetMe({
    request: authOpts,
    query: { enabled: !!token, retry: false },
  });

  useEffect(() => {
    if (!meLoading && me && !(me as any).featureFlags?.purchase_orders) {
      setLocation('/');
    }
  }, [meLoading, me]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [suppliers, setSuppliers] = useState<PoSupplier[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'sent' | 'received'>('all');
  const [loading, setLoading] = useState(true);

  // New PO dialog
  const [showNewPoDialog, setShowNewPoDialog] = useState(false);
  const [newPoSupplierId, setNewPoSupplierId] = useState('');
  const [newPoBusy, setNewPoBusy] = useState(false);

  // Create from order dialog
  const [showFromOrderDialog, setShowFromOrderDialog] = useState(false);
  const [fromOrderName, setFromOrderName] = useState('');
  const [fromOrderBusy, setFromOrderBusy] = useState(false);
  const [fromOrderResult, setFromOrderResult] = useState<{ createdPoIds: string[]; unmatchedCount: number; unmatched: { title: string; vendor: string }[] } | null>(null);

  // Delete dialog
  const [deletePoId, setDeletePoId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // ── Load data ──────────────────────────────────────────────────────────────

  async function loadStores() {
    const res = await fetch(`${BASE}/api/stores`, { headers });
    const data = await res.json();
    const list: ShopifyStore[] = Array.isArray(data) ? data : [];
    setStores(list);
    if (list.length > 0 && !selectedStoreId) {
      setSelectedStoreId(list[0]!.id);
    }
    return list;
  }

  async function loadOrdersAndSuppliers(storeId: string, status: string) {
    if (!storeId) return;
    setLoading(true);
    try {
      const [ordRes, supRes] = await Promise.all([
        fetch(`${BASE}/api/po/orders?storeId=${storeId}&status=${status}`, { headers }),
        fetch(`${BASE}/api/po/suppliers?storeId=${storeId}`, { headers }),
      ]);
      const ordData = await ordRes.json();
      const supData = await supRes.json();
      setOrders(ordData.orders ?? []);
      setSuppliers(supData.suppliers ?? []);
    } catch {
      toast({ title: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      const list = await loadStores();
      if (list.length > 0) {
        await loadOrdersAndSuppliers(list[0]!.id, statusFilter);
      } else {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (selectedStoreId) {
      loadOrdersAndSuppliers(selectedStoreId, statusFilter);
    }
  }, [selectedStoreId, statusFilter]);

  // ── New PO ─────────────────────────────────────────────────────────────────

  async function handleCreateNewPo() {
    if (!newPoSupplierId) {
      toast({ title: 'Select a supplier first', variant: 'destructive' });
      return;
    }
    setNewPoBusy(true);
    try {
      const res = await fetch(`${BASE}/api/po/orders`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, supplierId: newPoSupplierId, lineItems: [] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to create PO');
      setShowNewPoDialog(false);
      setNewPoSupplierId('');
      setLocation(`/po/purchase-orders/${data.order.id}/edit`);
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setNewPoBusy(false);
    }
  }

  // ── Create from order ──────────────────────────────────────────────────────

  async function handleFromOrder() {
    if (!fromOrderName.trim()) {
      toast({ title: 'Enter an order number first', variant: 'destructive' });
      return;
    }
    setFromOrderBusy(true);
    setFromOrderResult(null);
    try {
      const res = await fetch(`${BASE}/api/po/orders/from-order`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, orderName: fromOrderName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to create POs from order');

      if (data.createdPoIds.length === 1) {
        // Single PO — navigate directly to edit
        setShowFromOrderDialog(false);
        setFromOrderName('');
        toast({ title: `PO created from order ${fromOrderName}` });
        setLocation(`/po/purchase-orders/${data.createdPoIds[0]}/edit`);
      } else {
        // Multiple POs — show summary
        setFromOrderResult(data);
        await loadOrdersAndSuppliers(selectedStoreId, statusFilter);
      }
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setFromOrderBusy(false);
    }
  }

  // ── Delete PO ──────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (!deletePoId) return;
    setDeleteBusy(true);
    try {
      const res = await fetch(`${BASE}/api/po/orders/${deletePoId}`, { method: 'DELETE', headers });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? 'Delete failed');
      }
      toast({ title: 'Purchase order deleted' });
      setDeletePoId(null);
      await loadOrdersAndSuppliers(selectedStoreId, statusFilter);
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setDeleteBusy(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading && orders.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const tabs = ['all', 'draft', 'sent', 'received'] as const;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-primary" />
              Purchase Orders
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Generate and manage purchase orders for your suppliers.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => { setFromOrderName(''); setFromOrderResult(null); setShowFromOrderDialog(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-border text-foreground hover:bg-muted/50 transition-colors"
            >
              <Search className="w-3.5 h-3.5" />
              From Order
            </button>
            <button
              onClick={() => { setNewPoSupplierId(suppliers[0]?.id ?? ''); setShowNewPoDialog(true); }}
              disabled={suppliers.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-3.5 h-3.5" />
              New PO
            </button>
          </div>
        </div>

        <ShopifyTargetStoreSelector
          stores={stores}
          value={selectedStoreId}
          onChange={setSelectedStoreId}
          label="Shopify store"
          className="max-w-sm"
        />

        {/* No suppliers warning */}
        {suppliers.length === 0 && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3 text-sm text-amber-400">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              No PO suppliers configured yet.{' '}
              <a href={`${BASE}/po/suppliers`} className="underline hover:no-underline">
                Set up suppliers
              </a>{' '}
              before creating purchase orders.
            </div>
          </div>
        )}

        {/* Status filter tabs */}
        <div className="flex gap-1 border-b border-border pb-0">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setStatusFilter(tab)}
              className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                statusFilter === tab
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Orders table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-muted/30 border border-border rounded-xl p-10 text-center text-muted-foreground">
            <Package className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">No purchase orders yet</p>
            <p className="text-xs mt-1">
              {statusFilter !== 'all'
                ? `No ${statusFilter} purchase orders.`
                : 'Create your first PO using the buttons above.'}
            </p>
          </div>
        ) : (
          <div className="bg-muted/30 border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-4 py-3 text-xs font-medium text-muted-foreground">PO Number</th>
                  <th className="px-4 py-3 text-xs font-medium text-muted-foreground">Supplier</th>
                  <th className="px-4 py-3 text-xs font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-xs font-medium text-muted-foreground">Items</th>
                  <th className="px-4 py-3 text-xs font-medium text-muted-foreground">Order Ref</th>
                  <th className="px-4 py-3 text-xs font-medium text-muted-foreground">Created</th>
                  <th className="px-4 py-3 text-xs font-medium text-muted-foreground w-8" />
                </tr>
              </thead>
              <tbody>
                {orders.map((po, idx) => (
                  <tr
                    key={po.id}
                    className={`border-b border-border last:border-0 hover:bg-muted/40 cursor-pointer transition-colors ${idx % 2 === 0 ? '' : 'bg-muted/20'}`}
                    onClick={() => setLocation(`/po/purchase-orders/${po.id}/edit`)}
                  >
                    <td className="px-4 py-3 font-mono text-xs font-medium text-foreground">
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        {po.poNumber}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      <div className="flex items-center gap-1">
                        {po.supplierName}
                        {po.supplierDeletedNote && (
                          <span className="text-[10px] text-amber-400/70 ml-1">(deleted)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={po.status} />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {po.itemCount} {po.itemCount === 1 ? 'item' : 'items'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {po.sourceOrderName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {formatDate(po.createdAt)}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setLocation(`/po/purchase-orders/${po.id}/edit`)}
                          title="Open"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                        {po.status === 'draft' && (
                          <button
                            className="p-1 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                            onClick={() => setDeletePoId(po.id)}
                            title="Delete draft"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── New PO Dialog ─────────────────────────────────────────────────── */}
      {showNewPoDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl space-y-4">
            <h2 className="text-base font-semibold text-foreground">New Purchase Order</h2>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Supplier</label>
              <select
                value={newPoSupplierId}
                onChange={(e) => setNewPoSupplierId(e.target.value)}
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground"
              >
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setShowNewPoDialog(false)}
                className="flex-1 px-4 py-2 rounded-lg text-sm border border-border text-foreground hover:bg-muted/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewPo}
                disabled={newPoBusy || !newPoSupplierId}
                className="flex-1 px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {newPoBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Create PO
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create from Order Dialog ──────────────────────────────────────── */}
      {showFromOrderDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl space-y-4">
            <div>
              <h2 className="text-base font-semibold text-foreground">Create POs from Shopify Order</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Enter a Shopify order number. Line items will be matched to your suppliers by vendor name and split into separate POs.
              </p>
            </div>
            {!fromOrderResult ? (
              <>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Order Number</label>
                  <input
                    type="text"
                    placeholder="e.g. #1001 or 1001"
                    value={fromOrderName}
                    onChange={(e) => setFromOrderName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleFromOrder()}
                    className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowFromOrderDialog(false); setFromOrderName(''); }}
                    className="flex-1 px-4 py-2 rounded-lg text-sm border border-border text-foreground hover:bg-muted/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleFromOrder}
                    disabled={fromOrderBusy || !fromOrderName.trim()}
                    className="flex-1 px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {fromOrderBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Create POs
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-sm text-emerald-400">
                    Created {fromOrderResult.createdPoIds.length} purchase order{fromOrderResult.createdPoIds.length !== 1 ? 's' : ''} from order {fromOrderName}.
                  </div>
                  {fromOrderResult.unmatchedCount > 0 && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-400">
                      <p className="font-medium mb-1">{fromOrderResult.unmatchedCount} unmatched line item{fromOrderResult.unmatchedCount !== 1 ? 's' : ''}:</p>
                      <ul className="space-y-0.5">
                        {fromOrderResult.unmatched.map((u, i) => (
                          <li key={i}>{u.title} {u.vendor ? `(${u.vendor})` : ''}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { setShowFromOrderDialog(false); setFromOrderName(''); setFromOrderResult(null); }}
                  className="w-full px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      <AlertDialog open={!!deletePoId} onOpenChange={(open) => !open && setDeletePoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete purchase order?</AlertDialogTitle>
            <AlertDialogDescription>
              This draft PO will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
